// ========================================
// CONTEÚDO BLOQUEADO — VALIDAÇÃO NO FRONTEND
// src/utils/conteudoBloqueado.js
//
// Espelha as regras de backend/middleware/security.js para que o usuário receba
// o alerta ANTES de enviar o pedido: em vez de "Erro ao salvar - requisição
// bloqueada por motivos de segurança", ele lê "a Observação do Pedido tem '--',
// troque por um traço simples e salve novamente".
//
// Se as regras mudarem no backend, atualize as duas listas juntas.
// ========================================

// Cada regra explica, em linguagem de usuário final, o que foi digitado.
export const REGRAS_CONTEUDO = [
    {
        regex: /<\s*\/?\s*script\b/i,
        oQueFoi: 'a tag "<script>"',
        comoCorrigir: 'Remova esse trecho do texto.'
    },
    {
        regex: /javascript\s*:/i,
        oQueFoi: 'o texto "javascript:"',
        comoCorrigir: 'Remova "javascript:" do texto.'
    },
    {
        regex: /vbscript\s*:/i,
        oQueFoi: 'o texto "vbscript:"',
        comoCorrigir: 'Remova "vbscript:" do texto.'
    },
    {
        regex: /data\s*:\s*text\/html/i,
        oQueFoi: 'um link "data:text/html"',
        comoCorrigir: 'Remova esse link do texto.'
    },
    {
        regex: /<\s*iframe\b/i,
        oQueFoi: 'a tag "<iframe>"',
        comoCorrigir: 'Remova esse trecho do texto.'
    },
    {
        regex: /<\s*(object|embed)\b/i,
        oQueFoi: 'uma tag de mídia incorporada ("<object>" ou "<embed>")',
        comoCorrigir: 'Remova esse trecho do texto.'
    },
    {
        // Só bloqueia atributo de evento DENTRO de uma tag HTML (<img ... onerror=).
        // Texto comum como "Desconto = 5%" ou "Montagem = por conta do cliente" passa.
        regex: /<[^>]*\son\w+\s*=/i,
        oQueFoi: 'um atributo de evento HTML (por exemplo "onclick=" ou "onerror=")',
        comoCorrigir: 'Remova a tag HTML do texto.'
    },
    {
        regex: /\beval\s*\(/i,
        oQueFoi: 'a expressão "eval("',
        comoCorrigir: 'Remova "eval(" do texto.'
    },
    {
        regex: /\bexpression\s*\(/i,
        oQueFoi: 'a expressão "expression("',
        comoCorrigir: 'Remova "expression(" do texto.'
    },
    {
        // "--" é o marcador de comentário do PostgreSQL.
        regex: /--/,
        oQueFoi: 'o traço duplo ( -- )',
        comoCorrigir: 'Troque por um traço simples ( - ) ou remova um dos traços.'
    },
    {
        regex: /\bunion\s+(all\s+)?select\b/i,
        oQueFoi: 'o comando de banco de dados "UNION SELECT"',
        comoCorrigir: 'Reescreva o texto sem esse comando.'
    },
    {
        regex: /\binsert\s+into\s+\w/i,
        oQueFoi: 'o comando de banco de dados "INSERT INTO"',
        comoCorrigir: 'Reescreva o texto sem esse comando.'
    },
    {
        regex: /\bdelete\s+from\s+\w/i,
        oQueFoi: 'o comando de banco de dados "DELETE FROM"',
        comoCorrigir: 'Reescreva o texto sem esse comando.'
    },
    {
        regex: /\bdrop\s+(table|database)\b/i,
        oQueFoi: 'o comando de banco de dados "DROP"',
        comoCorrigir: 'Reescreva o texto sem esse comando.'
    },
    {
        regex: /\balter\s+table\b/i,
        oQueFoi: 'o comando de banco de dados "ALTER TABLE"',
        comoCorrigir: 'Reescreva o texto sem esse comando.'
    },
    {
        regex: /\btruncate\s+table\b/i,
        oQueFoi: 'o comando de banco de dados "TRUNCATE TABLE"',
        comoCorrigir: 'Reescreva o texto sem esse comando.'
    },
    {
        regex: /\bxp_cmdshell\b/i,
        oQueFoi: 'a procedure "xp_cmdshell"',
        comoCorrigir: 'Reescreva o texto sem esse comando.'
    },
    {
        regex: /\bexec(ute)?\s+(s|x)p_\w+/i,
        oQueFoi: 'uma chamada de procedure ("EXEC sp_...")',
        comoCorrigir: 'Reescreva o texto sem esse comando.'
    },
];

// Senha e token nunca são validados: são segredos, não vão para query nem para
// a tela. Validar aqui impedia login de quem tinha "--" na senha e ainda faria
// o trecho da senha aparecer na mensagem de erro.
const CAMPOS_IGNORADOS = new Set([
    'senha', 'senha_atual', 'nova_senha', 'confirmar_senha', 'senha_provisoria',
    'password', 'token', 'refresh_token', 'access_token', 'csrf_token', 'senha_hash'
]);

const NOMES_AMIGAVEIS = {
    observacao: 'Observação',
    observacao_pedido: 'Observação do Pedido',
    observacao_pagamento: 'Observação do Pagamento',
    observacao_pagamento_1: 'Observação do Pagamento 1',
    observacao_pagamento_2: 'Observação do Pagamento 2',
    observacao_pagamento_3: 'Observação do Pagamento 3',
    observacao_pagamento_4: 'Observação do Pagamento 4',
    observacao_aprovacao: 'Observação da Aprovação',
    aprovacao_orcamento_observacao: 'Observação da Aprovação',
    observacao_cancelamento: 'Observação do Cancelamento',
    observacao_entrega: 'Observação da Entrega',
    endereco: 'Endereço',
    endereco_entrega: 'Endereço de Entrega',
    bairro: 'Bairro',
    bairro_entrega: 'Bairro de Entrega',
    cidade: 'Cidade',
    cidade_entrega: 'Cidade de Entrega',
    estado: 'Estado',
    estado_entrega: 'Estado de Entrega',
    complemento: 'Complemento',
    referencia: 'Ponto de Referência',
    nome: 'Nome',
    nome_completo: 'Nome Completo',
    razao_social: 'Razão Social',
    nome_fantasia: 'Nome Fantasia',
    cnpj: 'CNPJ',
    email: 'E-mail',
    telefone: 'Telefone',
    descricao: 'Descrição',
    produto_nome: 'Nome do Produto',
    produto_codigo: 'Código do Produto',
    familia: 'Família do Produto',
    familia_produto: 'Família do Produto',
    grupo_produto: 'Grupo do Produto',
    tipo: 'Tipo',
    tipo_tabela: 'Tabela',
    forros: 'Forros',
    acessorios: 'Acessórios',
    produtos_especiais: 'Produtos Especiais',
    itens: 'Itens',
    departamento: 'Departamento',
    cargo: 'Cargo',
    regiao_acesso: 'Região de Acesso',
    vendedor: 'Vendedor',
    motivo: 'Motivo',
    justificativa: 'Justificativa',
    numero_proposta: 'Número da Proposta'
};

// Mostra o pedaço do texto onde está o problema, para o usuário localizar no campo.
const destacarTrecho = (texto, indice, tamanho) => {
    const inicio = Math.max(0, indice - 20);
    const fim = Math.min(texto.length, indice + tamanho + 20);
    const prefixo = inicio > 0 ? '...' : '';
    const sufixo = fim < texto.length ? '...' : '';

    return prefixo + texto.slice(inicio, fim).trim() + sufixo;
};

// ['forros', '[2]', 'produto_nome'] vira "Forros (item 3) → Nome do Produto"
export const rotularCaminho = (caminho) => {
    const partes = [];

    for (const item of caminho) {
        const indiceArray = /^\[(\d+)\]$/.exec(item);

        if (indiceArray) {
            const posicao = parseInt(indiceArray[1], 10) + 1;

            if (partes.length > 0) {
                partes[partes.length - 1] = `${partes[partes.length - 1]} (item ${posicao})`;
            } else {
                partes.push(`item ${posicao}`);
            }
            continue;
        }

        partes.push(NOMES_AMIGAVEIS[item] || item.replace(/_/g, ' '));
    }

    return partes.join(' → ');
};

const procurar = (valor, caminho) => {
    if (valor === null || valor === undefined) return null;

    if (Array.isArray(valor)) {
        for (let i = 0; i < valor.length; i++) {
            const achado = procurar(valor[i], [...caminho, `[${i}]`]);
            if (achado) return achado;
        }
        return null;
    }

    if (typeof valor === 'object') {
        for (const chave of Object.keys(valor)) {
            if (CAMPOS_IGNORADOS.has(chave.toLowerCase())) continue;

            const achado = procurar(valor[chave], [...caminho, chave]);
            if (achado) return achado;
        }
        return null;
    }

    if (typeof valor !== 'string') return null;

    for (const regra of REGRAS_CONTEUDO) {
        const encontrado = regra.regex.exec(valor);

        if (encontrado) {
            return {
                caminho,
                motivo: regra.oQueFoi,
                comoCorrigir: regra.comoCorrigir,
                trecho: destacarTrecho(valor, encontrado.index, encontrado[0].length)
            };
        }
    }

    return null;
};

/**
 * Procura conteúdo não permitido em qualquer campo do objeto enviado.
 * Retorna null quando está tudo certo, ou { campo, motivo, comoCorrigir, trecho, message }.
 */
export const encontrarConteudoBloqueado = (dados) => {
    const achado = procurar(dados, []);
    if (!achado) return null;

    const campo = rotularCaminho(achado.caminho) || 'um dos campos preenchidos';

    return {
        campo,
        motivo: achado.motivo,
        comoCorrigir: achado.comoCorrigir,
        trecho: achado.trecho,
        message:
            `O campo "${campo}" contém ${achado.motivo}, que não é permitido por segurança. ` +
            `${achado.comoCorrigir} Trecho encontrado: "${achado.trecho}"`
    };
};

/**
 * Versão simples para um texto só — útil em validação de campo individual.
 */
export const textoTemConteudoBloqueado = (texto) => {
    if (typeof texto !== 'string') return false;

    return REGRAS_CONTEUDO.some(regra => regra.regex.test(texto));
};

export default {
    REGRAS_CONTEUDO,
    encontrarConteudoBloqueado,
    textoTemConteudoBloqueado,
    rotularCaminho
};
